
import Vue, { PropType } from "vue";
import { getBrandConfig } from "@/config/brands";
import { Category } from "@/config/types";
import { getLauncherUrl, getOWCasinoId } from "@/services/gameLauncher";

export default Vue.extend({
  name: "GamesGrid",
  props: {
    selectedCategory: {
      type: String,
      default: null,
    },
    searchQuery: {
      type: String,
      default: "",
    },
    currentBrandId: {
      type: String,
      required: true,
    },
    brandFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    typeFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    integrationFilter: {
      type: String as PropType<string | null>,
      default: null,
    },
    launchingGameName: {
      type: String as PropType<string | null>,
      default: null,
    },
  },
  data() {
    return {
      categoryGames: {} as Record<string, any[]>,
      copyState: {} as Record<string, "loading">,
      copyTimers: {} as Record<string, ReturnType<typeof setTimeout>>,
      toastVisible: false,
      toastMessage: "",
      toastTimer: null as ReturnType<typeof setTimeout> | null,

      launcherReady: false,
      pendingResolve: null as null | (({ url, launchGameData }: { url: string, launchGameData: any }) => void),
      pendingReject: null as null | ((err: Error) => void),
    };
  },
  computed: {
    launcherUrl(): string {
      // URL where the launcher HTML page is hosted
      return "https://stellar-dasik-199fb1.netlify.app";
    },
    brandConfig() {
      return getBrandConfig(this.currentBrandId);
    },
    allGames(): any[] {
      const games: any[] = [];

      this.brandConfig.categories.forEach((category: Category) => {
        const categoryGames = this.categoryGames[category.name] || [];
        games.push(
          ...categoryGames.map((game: any) => ({
            ...game,
            category: category.name,
            gameType: game.gameType,
          }))
        );
      });

      return games;
    },
    games(): any[] {
      let filteredGames = this.allGames;

      if (this.selectedCategory) {
        filteredGames = filteredGames.filter(
          (game) => game.category === this.selectedCategory
        );
      }

      if (this.searchQuery && this.searchQuery.trim() !== "") {
        const query = this.searchQuery.toLowerCase().trim();
        filteredGames = filteredGames.filter(
          (game) =>
            game.name.toLowerCase().includes(query) ||
            game.category.toLowerCase().includes(query)
        );
      }

      if (this.brandFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.brand === this.brandFilter
        );
      }

      if (this.typeFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.gameType === this.typeFilter
        );
      }

      if (this.integrationFilter) {
        filteredGames = filteredGames.filter(
          (g: any) => g.integration === this.integrationFilter
        );
      }

      if (this.brandConfig.hideDisabled) {
        filteredGames = filteredGames.filter(
          (game) => !this.isGameDisabled(game)
        );
      }

      return filteredGames.slice().sort((a, b) => {
        const aDisabled = this.isGameDisabled(a) ? 1 : 0;
        const bDisabled = this.isGameDisabled(b) ? 1 : 0;
        if (aDisabled !== bDisabled) return aDisabled - bDisabled;
        return a.name.localeCompare(b.name);
      });
    },
  },
  watch: {
    currentBrandId: {
      immediate: true,
      handler() {
        this.loadGames();
      },
    },
  },
  methods: {
    loadGames() {
      this.categoryGames = {};
      const config = getBrandConfig(this.currentBrandId);

      config.categories.forEach((category: Category) => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const raw = require(`@/data/${category.gamesPath}`);
          let games: any[];
          if (Array.isArray(raw)) {
            games = raw;
          } else if (category.ipKeys) {
            games = category.ipKeys.flatMap((key) =>
              (raw[key] || []).map((g: any) => ({ ...g, brand: key }))
            );
          } else {
            games = Object.entries(raw).flatMap(([ip, ipGames]) =>
              (ipGames as any[]).map((g: any) => ({ ...g, brand: ip }))
            );
          }
          games = games.filter(
            (g: any) =>
              !g.accessOnly || g.accessOnly.includes(this.currentBrandId)
          );
          this.categoryGames[category.name] = games;
        } catch (e) {
          console.warn(`Games file not found: ${category.gamesPath}`);
          this.categoryGames[category.name] = [];
        }
      });
    },

    onIframeLoad() {
      // iframe loaded, waiting for 'ready' message from launcher
    },

    // handleGameClick(game: any) {
    //   if (this.launchingGameName) return;
    //   this.$emit('game-clicked', game);
    // },

    isGameDisabled(game: any): boolean {
      const files = game.gameFiles || [];
      const hasPlayable = files.some(
        (f: any) => f.actionType === "button" || f.actionType === "api-launch"
      );
      const hasDisabled = files.some(
        (f: any) =>
          f.actionType === "disabled" || f.actionType === "coming-soon"
      );
      return !hasPlayable && hasDisabled;
    },
    hasGameFile(game: any): boolean {
      return (game.gameFiles || []).some(
        (f: any) =>
          f.actionType !== "disabled" && f.actionType !== "coming-soon"
      );
    },
    showCopyToast(message: string) {
      this.toastMessage = message;
      this.toastVisible = true;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        this.toastVisible = false;
        this.toastTimer = null;
      }, 2000);
    },
    async copyGameLink(game: any): Promise<void> {
      if (this.copyState[game.name] === "loading") return;
      const file = (game.gameFiles || []).find(
        (f: any) => f.actionType !== "disabled"
      );
      if (!file) return;

      this.$set(this.copyState, game.name, "loading");
      try {
        let url: string;
        if (file.actionType === "api-launch") {
          url = await getLauncherUrl(
            file.gameId,
            file.launchProvider,
            file.url
          );
        } else {
          url = file.url;
        }
        await navigator.clipboard.writeText(url);
        this.showCopyToast("Copied to clipboard");
      } catch (e: any) {
        this.showCopyToast(e?.message || "Failed to copy link");
      } finally {
        this.$delete(this.copyState, game.name);
      }
    },
    getImageClass(imagePath: string): string {
      // List of images that should use object-fit: fill to avoid cropping
      const containImages = [
        "thumb-coming-soon.png",
        "coming-soon.png",
        "commingsoon",
        "placeholder.png",
      ];

      // Check if the image should use fill
      const shouldUseContain = containImages.some((img) =>
        imagePath.includes(img)
      );

      return shouldUseContain ? "contain-image" : "";
    },

    onMessage(event: MessageEvent) {
      if (!event.data || typeof event.data !== "object") return;

      switch (event.data.action) {
        case "ready":
          this.launcherReady = true;
          break;

        case "launch-result":
          if (this.pendingResolve) {
            this.pendingResolve({
              url: event.data.url,
              launchGameData: event.data.launchGameData
            });
            this.pendingResolve = null;
            this.pendingReject = null;
          }
          break;

        case "launch-error":
          if (this.pendingReject) {
            this.pendingReject(new Error(event.data.error));
            this.pendingResolve = null;
            this.pendingReject = null;
          }
          break;
      }
    },

    launchViaIframe(
      gameId: string,
      gameUrl?: string,
      casinoId?: number
    ): Promise<{ url: string, launchGameData: any }> {
      return new Promise((resolve, reject) => {
        const iframe = (this.$refs.launcherFrame as HTMLIFrameElement)
          ?.contentWindow;
        if (!iframe) {
          reject(new Error("Launcher iframe not available"));
          return;
        }

        this.pendingResolve = resolve;
        this.pendingReject = reject;

        iframe.postMessage(
          { action: "launch", gameId, gameUrl, casinoId },
          "*" // or restrict to your launcher origin
        );
      });
    },

    async handleGameClick(game: any) {
      if (this.launchingGameName) return;

      const file = (game.gameFiles || []).find(
        (f: any) => f.actionType === "api-launch"
      );
      if (!file) {
        // fallback for non-api-launch games
        this.$emit("game-clicked", game);
        return;
      }

      this.$emit("update:launchingGameName", game.name); // show spinner

      console.log(game)
      if(file.launchProvider == "oddswork"){
        const casinoId = getOWCasinoId(file.gameId);
        const {url, launchGameData} = await this.launchViaIframe(file.gameId, file.url, casinoId);
        // console.log(launchGameData)
        window.open(url, "_blank");
      } else {
        try {
          let url = file.url;
          if(!file.openDirectly) {
            const url = await getLauncherUrl(
              file.gameId,
              file.launchProvider,
              file.url
            );
          }
          window.open(url, "_blank"); // or however you want to open it
        } catch (err: any) {
          this.showCopyToast(err.message || "Launch failed");
        } finally {
          this.$emit("update:launchingGameName", null);
        }
      }
    },
  },

  mounted() {
    this.loadGames();
    window.addEventListener("message", this.onMessage);
  },

  beforeDestroy() {
    window.removeEventListener("message", this.onMessage);
    Object.values(this.copyTimers).forEach(clearTimeout);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
});
